import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

const ConfirmContext = createContext(null);

/**
 * Branded, promise-based replacement for window.confirm().
 *   const confirm = useConfirm();
 *   if (!(await confirm({ description, confirmText, destructive }))) return;
 * Alert-only (single OK, no cancel):
 *   await confirm({ description, alertOnly: true, confirmText: t('common.ok') });
 */
export function ConfirmProvider({ children }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts(options);
    setOpen(true);
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const close = (result) => {
    setOpen(false);
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  };

  const destructive = opts.destructive !== false; // default: destructive (red CTA)
  const alertOnly = Boolean(opts.alertOnly);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent className="rounded-2xl max-w-md" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${destructive ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}>
                <AlertTriangle className="h-5 w-5" />
              </span>
              <AlertDialogTitle data-testid="confirm-dialog-title">
                {opts.title || t('common.confirm_title')}
              </AlertDialogTitle>
            </div>
            {opts.description && (
              <AlertDialogDescription className="pt-1" data-testid="confirm-dialog-desc">
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {opts.note && (
            <div
              className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="confirm-dialog-note"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{opts.note}</span>
            </div>
          )}
          <AlertDialogFooter>
            {!alertOnly && (
              <AlertDialogCancel
                onClick={() => close(false)}
                data-testid="confirm-dialog-cancel"
                className={`rounded-xl ${opts.neutralCancel ? 'bg-secondary text-foreground border border-border hover:bg-secondary/80' : destructive ? 'bg-[#FFE0D6] text-[#B23A1E] border-transparent hover:bg-[#FFD3C4] hover:text-[#B23A1E]' : ''}`}
              >
                {opts.cancelText || t('common.cancel')}
              </AlertDialogCancel>
            )}
            <AlertDialogAction
              onClick={() => close(true)}
              data-testid="confirm-dialog-confirm"
              className={`rounded-xl ${alertOnly ? '' : destructive ? 'bg-destructive text-white hover:bg-destructive/90' : ''}`}
            >
              {opts.confirmText || (alertOnly ? t('common.ok') : t('common.confirm'))}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
